import { DEFAULT_ON_DECK_CONFIG, } from "./types";
import { checkType, extractItemsIfNeeded, extractListItems, fixQuotedArrays, hasValueAtPath, closeOpenBrackets, } from "./IncrementalJSONParser";
export function createStreamProcessingState() {
    return {
        currentJson: "",
        processedFields: new Set(),
        processedFullLists: new Set(),
        processedListItems: new Map(),
        onDeckDict: new Map(),
        toolCallAccumulators: new Map(),
    };
}
export function handleStreamChunk(operation, partialJson, state, operationType = "default", callbacks) {
    state.currentJson += partialJson;
    const firstBrace = state.currentJson.indexOf("{");
    if (firstBrace === -1) {
        return state.currentJson;
    }
    if (firstBrace > 0) {
        state.currentJson = state.currentJson.slice(firstBrace);
    }
    const maxInstanceCount = DEFAULT_ON_DECK_CONFIG[operationType] ?? DEFAULT_ON_DECK_CONFIG.default;
    try {
        const fixedQuotes = fixQuotedArrays(state.currentJson);
        const parsed = JSON.parse(fixedQuotes);
        processFieldsImmediate(operation, parsed, state, callbacks);
        processListFieldsImmediate(operation, parsed, state, callbacks);
        return state.currentJson;
    }
    catch {
        // fall through to tolerant parse
    }
    try {
        const fixedQuotes = fixQuotedArrays(state.currentJson);
        const closed = closeOpenBrackets(fixedQuotes);
        const parsed = JSON.parse(closed);
        processFields(operation, parsed, state, maxInstanceCount, callbacks);
        processListFields(operation, parsed, state, maxInstanceCount, callbacks);
    }
    catch (error) {
        callbacks?.onParseError?.(error instanceof Error ? error.message : String(error), state.currentJson);
    }
    return state.currentJson;
}
function processFieldsImmediate(operation, parsed, state, callbacks) {
    for (const field of operation.fieldsToCheck) {
        if (state.processedFields.has(field.name))
            continue;
        let value = parsed[field.name];
        if (value === undefined)
            continue;
        value = extractItemsIfNeeded(value);
        if (value === undefined || value === null)
            continue;
        if (!checkType(value, field.type))
            continue;
        operation.addStep(field.name, value);
        state.processedFields.add(field.name);
        callbacks?.onFieldParsed?.(field.name, value);
    }
}
function processListFieldsImmediate(operation, parsed, state, callbacks) {
    for (const field of operation.listsToCheck) {
        if (state.processedFullLists.has(field.name))
            continue;
        let value = parsed[field.name];
        value = extractItemsIfNeeded(value);
        if (!Array.isArray(value))
            continue;
        let processedIndices = state.processedListItems.get(field.name);
        if (!processedIndices) {
            processedIndices = new Set();
            state.processedListItems.set(field.name, processedIndices);
        }
        for (let i = 0; i < value.length; i += 1) {
            if (processedIndices.has(i))
                continue;
            const item = value[i];
            if (field.item_type && !checkType(item, field.item_type))
                continue;
            operation.addStep(field.name, item);
            processedIndices.add(i);
            callbacks?.onListItemParsed?.(field.name, i, item);
        }
        if (processedIndices.size === value.length && value.length > 0) {
            state.processedFullLists.add(field.name);
        }
    }
}
function processFields(operation, parsed, state, maxInstanceCount, callbacks) {
    for (const field of operation.fieldsToCheck) {
        if (state.processedFields.has(field.name))
            continue;
        let value = parsed[field.name];
        if (value === undefined)
            continue;
        value = extractItemsIfNeeded(value);
        if (value === undefined || value === null)
            continue;
        if (!checkType(value, field.type))
            continue;
        let onDeckItem = state.onDeckDict.get(field.name);
        if (!onDeckItem) {
            onDeckItem = { step_data: null, instance_number: 0 };
            state.onDeckDict.set(field.name, onDeckItem);
        }
        const currentStringified = JSON.stringify(value);
        const previousStringified = JSON.stringify(onDeckItem.step_data);
        if (currentStringified === previousStringified) {
            onDeckItem.instance_number += 1;
        }
        else {
            onDeckItem.step_data = value;
            onDeckItem.instance_number = 0;
        }
        if (onDeckItem.instance_number >= maxInstanceCount) {
            operation.addStep(field.name, onDeckItem.step_data);
            state.processedFields.add(field.name);
            callbacks?.onFieldParsed?.(field.name, onDeckItem.step_data);
        }
    }
}
function processListFields(operation, parsed, state, maxInstanceCount, callbacks) {
    for (const field of operation.listsToCheck) {
        let value = parsed[field.name];
        value = extractItemsIfNeeded(value);
        if (!Array.isArray(value)) {
            const rawValue = parsed[field.name];
            if (typeof rawValue === "string" && rawValue.startsWith("[")) {
                const extractedItems = extractListItems(rawValue, field.check_sub_field);
                if (extractedItems.length > 0) {
                    value = extractedItems;
                }
                else {
                    continue;
                }
            }
            else {
                continue;
            }
        }
        const valueArray = value;
        let processedIndices = state.processedListItems.get(field.name);
        if (!processedIndices) {
            processedIndices = new Set();
            state.processedListItems.set(field.name, processedIndices);
        }
        let fieldOnDeck = state.onDeckDict.get(field.name);
        if (!fieldOnDeck || !(fieldOnDeck instanceof Map)) {
            fieldOnDeck = new Map();
            state.onDeckDict.set(field.name, fieldOnDeck);
        }
        for (let i = 0; i < valueArray.length; i += 1) {
            if (processedIndices.has(i))
                continue;
            const item = valueArray[i];
            if (field.check_sub_field && !hasValueAtPath(item, field.check_sub_field)) {
                continue;
            }
            if (field.item_type && !checkType(item, field.item_type))
                continue;
            let itemOnDeck = fieldOnDeck.get(i);
            if (!itemOnDeck) {
                itemOnDeck = { step_data: null, instance_number: 0 };
                fieldOnDeck.set(i, itemOnDeck);
            }
            const currentStringified = JSON.stringify(item);
            const previousStringified = JSON.stringify(itemOnDeck.step_data);
            if (currentStringified === previousStringified) {
                itemOnDeck.instance_number += 1;
            }
            else {
                itemOnDeck.step_data = item;
                itemOnDeck.instance_number = 0;
            }
            if (itemOnDeck.instance_number >= maxInstanceCount) {
                operation.addStep(field.name, itemOnDeck.step_data);
                processedIndices.add(i);
                callbacks?.onListItemParsed?.(field.name, i, itemOnDeck.step_data);
            }
        }
        if (processedIndices.size === valueArray.length && valueArray.length > 0) {
            state.processedFullLists.add(field.name);
        }
    }
}
export function finalParse(state) {
    try {
        const fixedQuotes = fixQuotedArrays(state.currentJson);
        const closed = closeOpenBrackets(fixedQuotes);
        return JSON.parse(closed);
    }
    catch {
        return null;
    }
}
export function extractRemainingFields(operation, parsed, state, callbacks) {
    for (const field of operation.fieldsToCheck) {
        if (state.processedFields.has(field.name))
            continue;
        const value = parsed[field.name];
        if (value !== undefined && checkType(value, field.type)) {
            operation.addStep(field.name, value);
            state.processedFields.add(field.name);
            callbacks?.onFieldParsed?.(field.name, value);
        }
    }
    for (const field of operation.listsToCheck) {
        const value = parsed[field.name];
        if (!Array.isArray(value))
            continue;
        let processedIndices = state.processedListItems.get(field.name);
        if (!processedIndices) {
            processedIndices = new Set();
            state.processedListItems.set(field.name, processedIndices);
        }
        for (let i = 0; i < value.length; i += 1) {
            if (processedIndices.has(i))
                continue;
            const item = value[i];
            operation.addStep(field.name, item);
            processedIndices.add(i);
            callbacks?.onListItemParsed?.(field.name, i, item);
        }
    }
}
